import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FolderKanban, Calendar, CheckCircle, ChevronRight, Loader2, Building2, Search } from 'lucide-react';
import { projectsAPI } from '../services/api';
import { useCompanyStore } from '../context/companyStore';
import CompanyFilter from '../components/common/CompanyFilter';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastRefreshTimestamp = useRef(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');

  // Multi-company support
  const { selectedCompanyId, hasMultipleCompanies } = useCompanyStore();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['projects', selectedCompanyId],
    queryFn: () => projectsAPI.list({ companyId: selectedCompanyId }),
  });

  // Sync search to URL so it survives back-navigation from the detail page.
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    setSearchParams(params, { replace: true });
  }, [search, setSearchParams]);

  // Auto-refresh when returning from detail page
  useEffect(() => {
    const refreshTimestamp = location.state?.refresh;
    if (refreshTimestamp && refreshTimestamp !== lastRefreshTimestamp.current) {
      lastRefreshTimestamp.current = refreshTimestamp;
      setSearch(searchParams.get('search') || '');
      refetch();
    }
  }, [location.state?.refresh, refetch, searchParams]);

  const allProjects = data?.data?.projects || [];

  // Filter by search
  const projects = allProjects.filter(project => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return project.name?.toLowerCase().includes(searchLower) ||
           project.company?.name?.toLowerCase().includes(searchLower) ||
           project.status?.name?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Projects</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Track your ongoing projects</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <CompanyFilter />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLoading ? (
          <div className="col-span-2 p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="col-span-2 card p-8 text-center text-neutral-500 dark:text-neutral-400">
            <FolderKanban className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
            <p>No projects found</p>
          </div>
        ) : (
          projects.map((project) => (
            <div
              key={project.id}
              onClick={() => {
                sessionStorage.setItem('projectsReturnUrl', window.location.pathname + window.location.search);
                navigate(`/projects/${project.id}`);
              }}
              className="card p-5 cursor-pointer hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-neutral-900 dark:text-white truncate">{project.name}</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">#{project.id}</p>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <span className={`badge ${project.closedFlag ? 'badge-success' : 'badge-primary'}`}>
                    {project.status?.name || 'Active'}
                  </span>
                  <ChevronRight className="w-5 h-5 text-neutral-400" />
                </div>
              </div>
              <div className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Est. End: {project.estimatedEnd ? new Date(project.estimatedEnd).toLocaleDateString() : 'TBD'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Progress: {project.percentComplete || 0}%</span>
                </div>
                {hasMultipleCompanies && project.company?.name && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span>{project.company.name}</span>
                  </div>
                )}
              </div>
              {/* Progress bar */}
              <div className="mt-4 h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 transition-all"
                  style={{ width: `${project.percentComplete || 0}%` }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
